import {
    Directive,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { IdleStateTrackerService } from '@klickdata/core/application';
import { AuthService, CanComponentDeactivate } from '@klickdata/core/auth';
import { CustomField } from '@klickdata/core/custom-field';
import { Customer } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import {
    Resource,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
} from '@klickdata/core/resource';
import { Utils } from '@klickdata/core/util';
import { ResourceAccessControlSheetComponent } from '@klickdata/shared-components/src/resource-assignment/resource-access-control-sheet/resource-access-control-sheet.component';
import { ResourceAccessSpecificPermissionComponent } from '@klickdata/shared-components/src/resource-assignment/resource-access-specific-permission/resource-access-specific-permission.component';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, merge, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    defaultIfEmpty,
    distinctUntilChanged,
    filter,
    first,
    map,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { LiveEditorFieldLockService } from './live-editor-field-lock.service';
import { LiveEditorPresenceTrackerService } from './live-editor-presence-tracker.service';
import { ResourceSocketService } from './resource-ws.service';

@Directive()
export abstract class ResourceComposerDirective implements OnInit, OnDestroy, OnChanges, CanComponentDeactivate {
    @ViewChild('contentTemplateToBeInParent') contentTemplateToBeInParent: TemplateRef<any>;
    @ViewChild('extraTabTemplateToBeInParent') extraTabTemplateToBeInParent: TemplateRef<any>;
    @Input() resource: Resource | null;
    @Input() isAutoSave = true;
    @Input() disableRouting = false;
    @Output() resourceChange = new EventEmitter<Resource>();
    @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onResourcePromptStateChange: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Output() saved: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Output() onResKeysValidatyChanges: EventEmitter<{ [key: string]: string | boolean }[]> = new EventEmitter<
        { [key: string]: string | boolean }[]
    >();
    public authCustomer: Customer;
    public destroy: Subject<boolean> = new Subject<boolean>();
    public abstract typeId: ResourceTypes;
    public isAdmin$: Observable<boolean>;
    public formats: { [key: string]: string };
    public resourceForm: FormGroup;
    public autoSubmitAndDeactivate: boolean;
    public publish: boolean;
    public customFields$: Observable<CustomField[]>;
    private readonly liveEditorPresence = inject(LiveEditorPresenceTrackerService);
    private readonly liveEditorFieldLock = inject(LiveEditorFieldLockService);
    private readonly resourceSocketService = inject(ResourceSocketService);
    /** Drives ResourceUpdatedEvent subscription; null when no resource is open. */
    private readonly resourceListenId$ = new BehaviorSubject<number | null>(null);

    constructor(
        protected resourceService: ResourceService,
        protected categoryService: ResourceCategoryService,
        protected auth: AuthService,
        protected parentFormDirective: FormGroupDirective,
        protected messageService: MessageService,
        protected router: Router,
        protected bottomSheet: MatBottomSheet,
        protected idleStateTracker: IdleStateTrackerService
    ) {
        this.formats = {
            title: $localize`:@@title:Title`,
            description: $localize`:@@description:Description`,
            grade_system_id: $localize`:@@gradeSystem:Grade system`,
            categories: $localize`:@@categories:Categories`,
            start_date: $localize`:@@startDate:Start date`,
            end_date: $localize`:@@endDate:End date`,
            bullets: $localize`:@@summary:Summary`,
            article_code: $localize`:@@courseCode:Course code`,
            agenda: $localize`:@@agenda:Agenda`,
        };
    }
    ngOnInit(): void {
        this.isAdmin$ = this.auth.getUser().pipe(
            first(),
            map((user) => user.isAdmin())
        );
        this.customFields$ = this.auth
            .getCustomer()
            .pipe(
                map((customer) =>
                    customer.activeOrderedCustomFields.filter(
                        (cf) => ResourceTypes.scopeByType(this.typeId) === cf.scope_id
                    )
                )
            );
        this.auth
            .getCustomer()
            .pipe(takeUntil(this.destroy))
            .subscribe((customer) => (this.authCustomer = customer));
        this.resourceForm = this.parentFormDirective.form;
        this.resourceForm.valueChanges
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.onResKeysValidatyChanges.emit(this.getResKeysValidaty()));
        if (this.isAutoSave) {
            this.setupAutoSave();
        }
        this.syncLiveEditorPresence();
        this.listenToRemoteResourceUpdates();
    }

    /**
     * Remote resource patches: ResourceUpdatedEvent (WS/poll) + live-editor active/409.
     */
    private listenToRemoteResourceUpdates(): void {
        const socketUpdates$ = this.resourceListenId$.pipe(
            distinctUntilChanged(),
            switchMap((resId) =>
                resId
                    ? this.resourceSocketService
                          .listenToResourceUpdates({ res_id: resId })
                          .pipe(catchError(() => EMPTY))
                    : EMPTY
            )
        );

        merge(socketUpdates$, this.liveEditorFieldLock.resourcePatches$)
            .pipe(
                filter((resource): resource is Resource => !!resource?.id),
                takeUntil(this.destroy)
            )
            .subscribe((resource) => this.updateResource(resource, true));
    }

    /** Live-editor presence + field lock; also drives resource socket subscription. */
    private syncLiveEditorPresence(): void {
        const resId = this.resource?.id ?? null;
        if (resId && this.resourceForm) {
            this.liveEditorPresence.start(resId);
            this.liveEditorFieldLock.start(resId, this.resourceForm);
            this.resourceListenId$.next(resId);
        } else {
            this.liveEditorPresence.stop();
            this.liveEditorFieldLock.stop();
            this.resourceListenId$.next(null);
        }
    }

    setupAutoSave() {
        const idleState$ = this.idleStateTracker.startTracking();
        const formValue$ = this.resourceForm.valueChanges.pipe(debounceTime(5000));

        combineLatest([formValue$, idleState$])
            .pipe(
                takeUntil(this.destroy),
                filter(() => !!this.resourceForm.dirty && !!this.isAutoSave && !!this.idleStateTracker.isIdle()),
                map(([formValue]) => formValue),
                distinctUntilChanged((f1, f2) => Utils.isEqual(f1, f2))
            )
            .subscribe(() => {
                this.isAutoSave = true;
                this.liveEditorFieldLock.onComposerSave();
                this.updatePublishStatus();
                this.onSubmit();
            });
    }

    public submit() {
        this.isAutoSave = false;
        this.liveEditorFieldLock.onComposerSave();
        this.updatePublishStatus();
        this.onSubmit();
    }

    private updatePublishStatus() {
        this.publish = this.resourceForm.value?.publish;
        // this.resourceForm.value.users_attach?.length ||
        // this.resourceForm.value.users_detach?.length ||
        // this.resourceForm.value.groups_attach?.length ||
        // this.resourceForm.value.groups_detach?.length ||
        // this.resourceForm.value.sync_all_users ||
        // this.resourceForm.value.sync_all_groups;
    }

    public performResSubmit(params?: { [key: string]: any }) {
        if (this.autoSubmitAndDeactivate) {
            this.autoSubmitAndDeactivate = false;
            return;
        }
        this.liveEditorFieldLock.onComposerSave();
        this.prepareSubmit(params)
            .pipe(takeUntil(this.destroy))
            .subscribe((resource) => this.updateResource(resource));
    }
    public prepareSubmit(params?: { [key: string]: any }): Observable<Resource> {
        return this.buildPayload().pipe(
            switchMap((data) => this.createOrUpdate(data, params)),
            catchError((err) => {
                this.notifySaveError(err);
                return EMPTY;
            }),
            takeUntil(this.destroy)
        );
    }
    private notifySaveError(error: unknown): void {
        this.loading.emit(false);
        this.messageService.openMessage(
            MessageErrorComponent,
            this.extractServerErrorMessage(error) ?? $localize`Something went wrong while saving. Please try again.`
        );
    }
    private extractServerErrorMessage(error: unknown): string | null {
        const messages = this.readServerErrorMessages(error);
        if (!messages?.length) {
            return null;
        }
        return messages.join('\n');
    }
    private readServerErrorMessages(error: unknown): string[] | null {
        if (!error || typeof error !== 'object') {
            return null;
        }
        const errorRecord = error as Record<string, unknown>;
        const httpBody = errorRecord['error'];
        if (httpBody && typeof httpBody === 'object') {
            const fromHttpResponse = this.normalizeServerMessages((httpBody as Record<string, unknown>)['error']);
            if (fromHttpResponse) {
                return fromHttpResponse;
            }
            const fromErrorBody = this.normalizeServerMessages(httpBody);
            if (fromErrorBody) {
                return fromErrorBody;
            }
        }
        return this.normalizeServerMessages(errorRecord['error']);
    }
    private normalizeServerMessages(source: unknown): string[] | null {
        if (!source || typeof source !== 'object') {
            return null;
        }
        const messages = (source as Record<string, unknown>)['messages'];
        if (typeof messages === 'string') {
            return [messages];
        }
        if (!Array.isArray(messages) || !messages.length) {
            return null;
        }
        return messages.flatMap((message) => {
            if (typeof message === 'string') {
                return [message];
            }
            if (Array.isArray(message)) {
                return message.filter((part): part is string => typeof part === 'string');
            }
            return [];
        });
    }
    private buildPayload(): Observable<ResourceData> {
        return this.prepareForm().pipe(
            map((data) => {
                if (this.resourceForm.value.publish && this.resourceForm.get('publish').dirty) {
                    if ((data && data.published) || (this.resource && this.resource?.published)) {
                        data.last_publish = moment().format('YYYY-MM-DD HH:mm:ss');
                    } else {
                        data.published = moment().format('YYYY-MM-DD HH:mm:ss');
                    }
                }

                if (!this.resourceForm.value.publish && data) {
                    data.last_publish = null;
                }

                if (this.resourceForm.value.collaboration?.enroll_approval_required?.metadata?.occasion_dates?.length) {
                    data.collaboration.enroll_approval_required.metadata.occasion_dates = Utils.cleanOccasionDates(
                        this.resourceForm.value.collaboration.enroll_approval_required.metadata.occasion_dates
                    );
                }

                if (this.resourceForm.get('labelTranslation')?.dirty && this.resourceForm.value.labelTranslation) {
                    const labelTranslationValue = this.resourceForm.value.labelTranslation;
                    const collabValues = (this.resourceForm.get('collaboration') as FormGroup).value;
                    if (!collabValues?.view?.metadata) {
                        data.collaboration = {
                            view: {
                                metadata: { name_translation: labelTranslationValue },
                            },
                        };
                    } else {
                        data.collaboration.view.metadata = {
                            ...data.collaboration.view.metadata,
                            name_translation: labelTranslationValue,
                        };
                    }
                }

                return data;
            })
        );
    }
    public abstract onSubmit(): void;
    public abstract getResKeysValidaty(): { [key: string]: string | boolean }[];
    protected abstract prepareForm(): Observable<ResourceData>;
    public abstract checkSubmitValid(): Observable<boolean>;
    public abstract onTabSelectionChange(activeIndex: number): void;

    protected updateResource(resource: Resource, fromSocket = false) {
        const isNewResource = !this.resource || this.resource.id !== resource.id;
        const updates =
            !isNewResource && this.resource ? this.resource.getData(resource.getData()) : resource.getData();
        // Keep any locally dirty control (and the currently dominated field) so remote
        // patches never clobber in-progress edits — not only the focused field.
        const localOverrides = fromSocket ? this.collectLocalEditOverrides() : {};

        // SCORM/AI tag generation: keep the user's in-progress tags and append any newly
        // generated ones instead of letting either side wipe the other.
        if (fromSocket && 'tag_ids' in localOverrides) {
            localOverrides.tag_ids = this.mergeTagIds(
                localOverrides.tag_ids as number[] | null | undefined,
                updates.tag_ids as number[] | null | undefined
            );
        }

        this.resource = resource;
        this.resourceForm.patchValue(updates, { emitEvent: false });
        Object.entries(localOverrides).forEach(([path, value]) => {
            this.resourceForm.get(path)?.setValue(value, { emitEvent: false });
        });

        if (isNewResource) {
            this.handleRouting();
            FormHelper.resetForm(this.resourceForm);
        } else if (!fromSocket) {
            FormHelper.resetForm(this.resourceForm);
        } else {
            Object.keys(updates)
                .filter((key) => !(key in localOverrides))
                .forEach((key) => {
                    const ctrl = this.resourceForm.get(key);
                    if (ctrl) {
                        FormHelper.resetForm(ctrl);
                    }
                });
        }

        this.resourceChange.emit(resource);
        this.loading.emit(false);
        this.onResourceChanged(isNewResource);
        this.syncLiveEditorPresence();
        if (fromSocket) {
            this.onRemoteResourceUpdate(resource);
        }

        // Toast + saved only for the user's own save. Socket echoes of that save (or edits
        // by other live editors) must not re-toast or re-trigger saved-driven navigation.
        if (!this.isAutoSave && !fromSocket) {
            this.messageService.openMessage(MessageSavedComponent);
            this.saved.emit(this.resource);
        }
    }

    /**
     * Snapshot of every control the user is still editing locally.
     * Includes dirty controls and the live-editor dominated field (even if still pristine).
     */
    private collectLocalEditOverrides(): Record<string, unknown> {
        const overrides: Record<string, unknown> = {};
        const dominated = this.liveEditorFieldLock.getDominatedField();
        if (dominated != null) {
            const dominatedCtrl = this.resourceForm.get(dominated);
            if (dominatedCtrl) {
                overrides[dominated] = dominatedCtrl.value;
            }
        }

        const walk = (control: AbstractControl, path: string): void => {
            if (control instanceof FormGroup) {
                Object.entries(control.controls).forEach(([key, child]) => walk(child, path ? `${path}.${key}` : key));
                return;
            }
            if (control instanceof FormArray) {
                control.controls.forEach((child, index) => walk(child, `${path}.${index}`));
                return;
            }
            if (path && control.dirty && !(path in overrides)) {
                overrides[path] = control.value;
            }
        };

        walk(this.resourceForm, '');
        return overrides;
    }

    /** Union of local and remote tag ids, preserving order (local first). */
    private mergeTagIds(localTagIds: number[] | null | undefined, remoteTagIds: number[] | null | undefined): number[] {
        return [...new Set([...(localTagIds ?? []), ...(remoteTagIds ?? [])])];
    }

    /** Type-specific side effects after a remote ResourceUpdatedEvent (e.g. SCORM status). */
    protected onRemoteResourceUpdate(_resource: Resource): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.resource && this.resource) {
            const { previousValue: prevResource, currentValue: currResource } = changes.resource;
            const isNewResource = (!prevResource && currResource) || prevResource?.id !== currResource?.id;
            this.onResourceChanged(isNewResource);
            this.syncLiveEditorPresence();
            if (!!this.resource.prompt_states?.length) {
                setTimeout(() => {
                    this.onResourcePromptStateChange.emit(this.resource);
                }, 500);
            }
        }
    }

    public handleRouting() {
        if (!this.disableRouting) {
            const segments = this.router.url.split('/');
            segments.pop();
            segments.push(this.resource.id.toString());
            this.router.navigate(segments);
        }
    }

    public setDefaultCategory() {
        this.categoryService
            .getSuggestedCategory(this.typeId)
            .pipe(
                takeUntil(this.destroy),
                filter((cat) => cat && !!cat.id)
            )
            .subscribe((category) => {
                if (!this.resourceForm.controls.category_ids.value?.length) {
                    this.resourceForm.patchValue({
                        category_ids: [category.id],
                    });
                    FormHelper.resetForm(<FormControl>this.resourceForm.controls.category_ids);
                }
            });
    }
    public onResourceSuccess(resource: Resource) {
        this.loading.emit(false);
        if (this.isAutoSave && !this.resourceForm.value.id && resource.id) {
            const segments = this.router.url.split('/');
            segments.pop();
            segments.push(resource.id.toString());
            this.router.navigate(segments);
        }
        FormHelper.resetForm(this.resourceForm);
        if (!this.isAutoSave) {
            this.messageService.openMessage(MessageSavedComponent);
            this.resourceForm.reset();
            this.saved.emit(resource);
        }
    }
    public openMessage(component: any, message?: string): void {
        if (!this.isAutoSave) {
            this.messageService.openMessage(component, message);
        }
    }
    public canDeactivate(): boolean {
        const prisitneControls = Object.entries(this.resourceForm.controls).filter((val) => !val[1].pristine);
        if (prisitneControls.length === 0) {
            this.resourceForm.markAsPristine();
        }
        return this.resourceForm.pristine;
    }

    public saveAndDeactivate(): Observable<boolean> {
        this.autoSubmitAndDeactivate = true;
        this.liveEditorFieldLock.onComposerSave();
        this.updatePublishStatus();
        return this.checkSubmitValid().pipe(
            switchMap((isValid: boolean) =>
                isValid
                    ? this.prepareSubmit().pipe(
                          map(() => true),
                          defaultIfEmpty(false)
                      )
                    : of(false)
            )
        );
    }

    public ngOnDestroy(): void {
        this.liveEditorPresence.stop();
        this.liveEditorFieldLock.stop();
        this.resourceListenId$.next(null);
        this.resourceListenId$.complete();
        this.destroy.next(true);
        this.destroy.unsubscribe();
        this.idleStateTracker.destroyTracking();
    }
    abstract onResourceChanged(created: boolean): void;

    openCollaborationSheet() {
        this.prepareSubmit()
            .pipe(
                first(),
                tap((resource) => this.updateResource(resource)),
                switchMap((resource) => {
                    const sheet = this.bottomSheet.open(ResourceAccessControlSheetComponent, {
                        data: {
                            context: 'access_control',
                            contextTitle: $localize`Access Control`,
                            contextIcon: 'manage_accounts',
                            resource: resource,
                            title: resource.title,
                            hasEditPrivilege: true,
                        },
                        panelClass: 'sheet-wrapper',
                    });
                    return sheet.afterDismissed();
                }),
                takeUntil(this.destroy),
                filter((result) => !!result)
            )
            .subscribe(() => {
                this.messageService.openMessage(MessageSavedComponent);
                this.resourceForm.markAsDirty();
            });
    }

    editResourceAccessPermision(
        permType: string,
        sortingDir: string,
        saveResParams?: { [key: string]: any }
    ): Observable<any> {
        const perm = Utils.getAccessControlPermType(permType);
        return this.prepareSubmit().pipe(
            tap((resource) => this.updateResource(resource)),
            switchMap((resource) => {
                const sheet = this.bottomSheet.open(ResourceAccessSpecificPermissionComponent, {
                    data: {
                        perm: perm,
                        resource: resource,
                        sortingDir: sortingDir,
                        customer: this.authCustomer,
                        title: resource.title,
                        saveResParams: saveResParams,
                    },
                    panelClass: 'sheet-wrapper',
                });
                return sheet.afterDismissed();
            }),
            takeUntil(this.destroy),
            filter((resource) => !!resource?.collaboration),
            tap((resource) => {
                this.resourceForm.get('collaboration').patchValue(resource.collaboration);
                this.messageService.openMessage(MessageSavedComponent);
            })
        );
    }

    protected createOrUpdate(data: ResourceData, params: { [key: string]: any } = {}) {
        if (data.id) {
            const { eager = [] } = params;
            const parsedEager = typeof eager === 'string' ? eager.split(',') : eager;

            return this.patchResource(data, {
                ...params,
                eager: [...parsedEager, 'publication', 'publishment', 'editors'],
            });
        } else {
            return this.storeResource(data, params);
        }
    }

    protected storeResource(data: ResourceData, params?: { [key: string]: any }): Observable<Resource> {
        return this.resourceService.store(data, params);
    }

    protected patchResource(data: ResourceData, params?: { [key: string]: any }): Observable<Resource> {
        return this.resourceService.update(data, true, params);
    }
}
